import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsAPI } from '../services/api';

const DEFAULT_SETTINGS = {
  site_title: "Haber Türkiye'den",
  site_tagline: "Türkiye'den",
  meta_description: "Türkiye'nin önde gelen ekonomi ve iş dünyası haber portalı.",
  head_code: '',
  logo_data: null,
  favicon_data: null,
  categories: [
    { slug: 'ekonomi', label: 'Ekonomi', visible: true },
    { slug: 'politika', label: 'Politika', visible: true },
    { slug: 'teknoloji', label: 'Teknoloji', visible: true },
    { slug: 'finans', label: 'Finans', visible: true },
    { slug: 'dunya', label: 'Dünya', visible: true },
  ],
  social: { facebook: '', twitter: '', instagram: '', youtube: '', linkedin: '' },
};

const SettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  loaded: false,
  refresh: () => {},
});

export const useSettings = () => useContext(SettingsContext);

// Keep the <link rel="icon"> tag in sync with favicon_data
const applyFavicon = (dataUrl) => {
  if (!dataUrl) return;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = dataUrl;
};

const HEAD_INJECTION_ATTR = 'data-haber-head-inject';

/**
 * Inject admin-provided raw HTML (Google verification, analytics, etc.) into <head>.
 * - Removes previously injected nodes (idempotent across refreshes)
 * - Recreates <script> nodes so inline scripts actually execute
 */
const applyHeadCode = (code) => {
  // Remove old injection
  document.head.querySelectorAll('[' + HEAD_INJECTION_ATTR + ']').forEach((el) => el.remove());
  if (!code || !code.trim()) return;
  try {
    const doc = new DOMParser().parseFromString(`<head>${code}</head>`, 'text/html');
    Array.from(doc.head.childNodes).forEach((node) => {
      if (node.nodeType !== 1) return; // element nodes only
      let clone;
      if (node.tagName === 'SCRIPT') {
        // Recreate script to ensure execution
        clone = document.createElement('script');
        for (const attr of node.attributes) clone.setAttribute(attr.name, attr.value);
        if (node.textContent) clone.text = node.textContent;
      } else {
        clone = node.cloneNode(true);
      }
      clone.setAttribute(HEAD_INJECTION_ATTR, '1');
      document.head.appendChild(clone);
    });
  } catch (e) {
    // Silently ignore malformed snippets
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await settingsAPI.get();
      const merged = { ...DEFAULT_SETTINGS, ...(res.data || {}) };
      if (merged.social) merged.social = { ...DEFAULT_SETTINGS.social, ...merged.social };
      setSettings(merged);
      if (merged.favicon_data) applyFavicon(merged.favicon_data);
      applyHeadCode(merged.head_code || '');
    } catch (e) {
      // Keep defaults on failure
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};
